import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import i18next from 'i18next'
import detercamLogo from '@/assets/detercam-logo.png'
import { useNavigate } from 'react-router-dom'
import { Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { authApi, twoFactorApi } from '@/api/endpoints'
import { isOtpChallenge, type User } from '@/types'
import { toast } from '@/lib/toast'
import { presentLoginError } from '@/lib/loginError'
import { presentResetError, sanitizeResetCode } from '@/lib/resetError'
import { getRecaptchaToken, preloadRecaptcha } from '@/lib/recaptcha'
import { Footer } from '@/components/layout/Footer'

type View = 'login' | 'forgot' | 'reset' | 'otp'

function PasswordField({
  id, value, onChange, placeholder, autoComplete, autoFocus,
}: {
  id: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  autoComplete?: string
  autoFocus?: boolean
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
        title={show ? 'Hide password' : 'Show password'}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

export function LoginPage() {
  const { t } = useTranslation('auth')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [view, setView] = useState<View>('login')

  // Login form
  const [loginData, setLoginData] = useState({ email: '', password: '' })

  // 2FA OTP step (superuser SMS 2FA)
  const [otp, setOtp] = useState({ challengeToken: '', hint: '', smsSent: true, code: '' })

  // Forgot password form
  const [forgotEmail, setForgotEmail] = useState('')

  // Reset password form
  const [resetData, setResetData] = useState({ otp: '', newPassword: '', confirmPassword: '' })

  // #240: reCAPTCHA — the server says whether it is on; the notice is shown only then.
  const [captchaOn, setCaptchaOn] = useState(false)
  useEffect(() => {
    let alive = true
    preloadRecaptcha().then((on) => { if (alive) setCaptchaOn(on) })
    return () => { alive = false }
  }, [])

  // Shared post-login side-effects (preference sync + welcome toast + navigate).
  // Reused by password login and the 2FA OTP verify step.
  const finishLogin = (user: User, access: string, refresh: string) => {
    login(user, access, refresh)

    // Sync dashboard preferences from user's stored profile
    // Use setState directly to avoid redundant PATCH back to server
    const uiState = useUIStore.getState()
    if (user.language && user.language !== i18next.language) {
      i18next.changeLanguage(user.language)
      document.documentElement.lang = user.language
    }
    // Always update UI store language so the toggle reflects the correct state
    if (user.language) {
      useUIStore.setState({ language: user.language })
    }
    if (user.theme && user.theme !== uiState.theme) {
      // Apply CSS class manually, then set store — avoids the PATCH in setTheme()
      const root = window.document.documentElement
      root.classList.remove('light', 'dark')
      if (user.theme === 'system') {
        root.classList.add(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      } else {
        root.classList.add(user.theme)
      }
      useUIStore.setState({ theme: user.theme })
    }

    toast.success(t('messages.welcomeBack'), t('messages.loggedInAs', { name: user.username }))
    navigate('/')
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsLoading(true)

    try {
      const recaptchaToken = await getRecaptchaToken('login')
      const response = await authApi.login({
        email: loginData.email.trim().toLowerCase(),
        password: loginData.password,
        device_type: 'web',
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      })

      // Superuser SMS 2FA: no tokens yet — move to the OTP step.
      if (isOtpChallenge(response)) {
        setOtp({
          challengeToken: response.challenge_token,
          hint: response.delivery.hint,
          smsSent: response.delivery.sms_sent,
          code: '',
        })
        setView('otp')
        if (!response.delivery.sms_sent) toast.error(t('twoFactor.smsFailed'))
        return
      }

      finishLogin(response.user, response.tokens.access, response.tokens.refresh)
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: string; error?: string } } }).response
      const view = presentLoginError(res?.status, res?.data)
      toast.error(t(view.titleKey), view.detail ?? (view.detailKey ? t(view.detailKey) : undefined),
                  { id: 'login-error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const res = await twoFactorApi.verifyLoginOtp(otp.challengeToken, otp.code.trim())
      finishLogin(res.user, res.tokens.access, res.tokens.refresh)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status
      if (status === 401) { toast.error(t('twoFactor.expired')); setView('login') }
      else if (status === 429) toast.error(t('twoFactor.cooldown'))
      else toast.error(t('twoFactor.wrongCode'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async (channel: 'sms' | 'email') => {
    try {
      await twoFactorApi.resendLoginOtp(otp.challengeToken, channel)
      toast.success(channel === 'email' ? t('twoFactor.emailSent') : t('twoFactor.resent'))
      if (channel === 'email') setOtp((o) => ({ ...o, smsSent: true }))
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status
      if (status === 401) { toast.error(t('twoFactor.expired')); setView('login') }
      else toast.error(t('twoFactor.cooldown'))
    }
  }

  // #211: both reset steps word a failure the same way; `title` and `fallback` are the step's own
  // words for a refusal the server gave no reason for.
  const showResetError = (err: unknown, title: string, fallback: string) => {
    const res = (err as { response?: { status?: number; data?: unknown } }).response
    const view = presentResetError(res?.status, res?.data)
    if (view.kind === 'wait') {
      toast.error(t('reset.tooManyAttempts'), view.waitMinutes
        ? t('reset.waitMinutes', { minutes: view.waitMinutes })
        : t('twoFactor.cooldown'))
    } else if (view.kind === 'unavailable') {
      toast.error(title, t('reset.serverUnavailable'))
    } else {
      toast.error(title, view.detail ?? fallback)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const recaptchaToken = await getRecaptchaToken('password_reset')
      await authApi.requestPasswordReset(forgotEmail, recaptchaToken)
      toast.success(t('messages.codeSent'), t('messages.resetCodeSent'))
      setView('reset')
    } catch (err: unknown) {
      showResetError(err, tc('error.title'), t('messages.sendResetFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error(tc('error.title'), t('messages.passwordMismatch'))
      return
    }

    // #211: two of the server's password rules can be checked here, before the request spends one
    // of the five reset calls an hour allows. The server stays the judge (it also rejects common
    // passwords, which only it can check).
    if (resetData.newPassword.length < 8 || /^\d+$/.test(resetData.newPassword)) {
      toast.error(tc('error.title'), t('reset.passwordRules'))
      return
    }

    setIsLoading(true)

    try {
      await authApi.confirmPasswordReset(
        forgotEmail, resetData.otp, resetData.newPassword, resetData.confirmPassword,
      )
      toast.success(t('messages.passwordReset'), t('messages.passwordResetSuccess'))
      setView('login')
      setResetData({ otp: '', newPassword: '', confirmPassword: '' })
    } catch (err: unknown) {
      showResetError(err, t('messages.resetFailed'), t('messages.resetFailedMessage'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-primary/5 via-background to-muted/30 p-4">

      {/* Elevated card */}
      <div className="w-full max-w-sm rounded-2xl border border-border/60 bg-background shadow-elevation-3 ring-1 ring-border/30">

        {/* Card header — keyed by view for entrance animation */}
        <div className="flex flex-col items-center px-8 pt-8 pb-6 border-b border-border/40">
          <img
            src={detercamLogo}
            alt="DeterCam"
            className="h-10 w-auto object-contain mb-5"
          />
          <div key={`header-${view}`} className="flex flex-col items-center animate-fade-up">
            {view === 'login' && (
              <>
                <h1 className="text-subheading">{t('login.title')}</h1>
                <p className="mt-1 text-body text-muted-foreground">{t('login.subtitle')}</p>
              </>
            )}
            {view === 'forgot' && (
              <>
                <h1 className="text-subheading">{t('forgot.title')}</h1>
                <p className="mt-1 text-body text-muted-foreground text-center">
                  {t('forgot.subtitle')}
                </p>
              </>
            )}
            {view === 'reset' && (
              <>
                <h1 className="text-subheading">{t('reset.title')}</h1>
                <p className="mt-1 text-body text-muted-foreground text-center">
                  {t('reset.subtitlePlain', { email: forgotEmail })}
                </p>
              </>
            )}
            {view === 'otp' && (
              <>
                <h1 className="text-subheading">{t('twoFactor.title')}</h1>
                <p className="mt-1 text-body text-muted-foreground text-center">
                  {t('twoFactor.subtitle', { hint: otp.hint })}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Card body — keyed by view for entrance animation */}
        <div key={`body-${view}`} className="px-8 py-6 animate-fade-up">

          {/* Login form */}
          {view === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="email" className="text-body font-medium">
                  {t('login.email')}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-body font-medium">
                    {t('login.password')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setView('forgot')}
                    className="text-label text-muted-foreground hover:text-primary transition-colors underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-primary"
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>
                <PasswordField
                  id="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full mt-2" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('login.signingIn')}
                  </>
                ) : (
                  t('login.signIn')
                )}
              </Button>
            </form>
          )}

          {/* Forgot password form */}
          {view === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="forgot-email" className="text-body font-medium">
                  {t('forgot.emailAddress')}
                </label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('forgot.sendingCode')}
                  </>
                ) : (
                  t('forgot.sendResetCode')
                )}
              </Button>

              <button
                type="button"
                onClick={() => setView('login')}
                className="flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('forgot.backToSignIn')}
              </button>
            </form>
          )}

          {/* Reset password form */}
          {view === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="otp" className="text-body font-medium">
                  {t('reset.resetCode')}
                </label>
                <Input
                  id="otp"
                  type="text"
                  placeholder={t('reset.codePlaceholder')}
                  value={resetData.otp}
                  onChange={(e) => setResetData({ ...resetData, otp: sanitizeResetCode(e.target.value) })}
                  inputMode="numeric"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="new-password" className="text-body font-medium">
                  {t('reset.newPassword')}
                </label>
                <PasswordField
                  id="new-password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={resetData.newPassword}
                  onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                  autoComplete="new-password"
                />
                <p className="text-sm text-muted-foreground">{t('reset.passwordRules')}</p>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="confirm-password" className="text-body font-medium">
                  {t('reset.confirmPassword')}
                </label>
                <PasswordField
                  id="confirm-password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                  autoComplete="new-password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('reset.resettingPassword')}
                  </>
                ) : (
                  t('reset.resetPassword')
                )}
              </Button>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                  className="hover:text-foreground transition-colors underline-offset-4 hover:underline disabled:opacity-50"
                >
                  {t('reset.resendCode')}
                </button>
                <button
                  type="button"
                  onClick={() => setView('login')}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('forgot.backToSignIn')}
                </button>
              </div>
            </form>
          )}

          {/* 2FA OTP step */}
          {view === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="otp-code" className="text-body font-medium">{t('twoFactor.codeLabel')}</label>
                <Input
                  id="otp-code"
                  type="text"
                  inputMode="numeric"
                  placeholder={t('twoFactor.codePlaceholder')}
                  value={otp.code}
                  onChange={(e) => setOtp({ ...otp, code: e.target.value })}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading || otp.code.trim().length === 0}>
                {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('twoFactor.verifying')}</>) : t('twoFactor.verify')}
              </Button>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <button type="button" onClick={() => handleResendOtp('sms')} className="hover:text-foreground transition-colors underline-offset-4 hover:underline">
                  {t('twoFactor.resend')}
                </button>
                <button type="button" onClick={() => handleResendOtp('email')} className="hover:text-foreground transition-colors underline-offset-4 hover:underline">
                  {t('twoFactor.sendByEmail')}
                </button>
              </div>
              <button type="button" onClick={() => setView('login')} className="flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" />{t('twoFactor.backToSignIn')}
              </button>
            </form>
          )}

          {/* #240: Google requires this notice where the badge is hidden (index.css). */}
          {captchaOn && view !== 'otp' && (
            <p className="mt-4 text-center text-xs text-muted-foreground">
              <Trans
                t={t}
                i18nKey="recaptcha.notice"
                components={{
                  privacy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground" />,
                  terms: <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground" />,
                }}
              />
            </p>
          )}
        </div>
      </div>

      <div className="mt-6">
        <Footer />
      </div>
    </div>
  )
}
